"use client";

import React from "react";
import { format } from "date-fns";
import { FileText, Calendar } from "lucide-react";
import { useEnhancedTimeline, type TimelineItem } from "@/lib/hooks/api/timeline/enhanced-timeline";
import { getChannelVisual } from "@/features/timeline/utils/channel-visuals";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";

interface MiniTimelineProps {
  email: string;
  contactId?: string;
  contactName?: string;
}

export const MiniTimeline: React.FC<MiniTimelineProps> = ({ email, contactId, contactName }) => {
  const { openModal } = useGlobalModalStack();
  const { data: timeline, isLoading } = useEnhancedTimeline(email, {
    limit: 5,
    enabled: !!email,
    contactId,
  });

  const handleQuickAction = (action: "note" | "meeting") => {
    if (action === "note") {
      openModal(MODAL_IDS.messages.logActivity, { contactId, contactEmail: email });
    } else {
      openModal(MODAL_IDS.meetings.create, { contactId });
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500 py-2">Loading recent interactions...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => handleQuickAction("note")}>
          <FileText className="h-3 w-3" /> Log Note
        </Button>
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => handleQuickAction("meeting")}>
          <Calendar className="h-3 w-3" /> Schedule
        </Button>
      </div>

      {!timeline || timeline.length === 0 ? (
        <div className="text-sm text-gray-500 py-2">No recent interactions.</div>
      ) : (
        <Accordion type="single" collapsible className="w-full space-y-2">
          {timeline.slice(0, 5).map((item: TimelineItem, index: number) => {
            const isHighPriority = item.priority === "high";
            const { Icon, iconClass } = getChannelVisual(item);

            return (
              <AccordionItem
                key={item.id}
                value={`item-${index}`}
                className={`border rounded-lg px-3 overflow-hidden bg-white border-l-4 dark:bg-gray-900 ${isHighPriority ? "border-l-amber-500" : "border-l-blue-500"}`}
              >
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center gap-3 w-full min-w-0">
                    <div className="flex-shrink-0">
                      <Icon className={`h-4 w-4 ${iconClass}`} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className={`truncate text-sm ${isHighPriority ? "font-bold text-gray-900 dark:text-gray-100" : "font-medium text-gray-800 dark:text-gray-200"}`}>
                        {item.title || "Interaction"}
                      </p>
                      <p className="text-[11px] text-gray-500 font-normal">
                        {format(new Date(item.occurred_at), "MMM d, h:mm a")}
                      </p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-2 text-xs text-gray-600 border-t pt-2 dark:text-gray-400">
                  <div className="space-y-1">
                    <p className="capitalize">Type: {item.type}</p>
                    {item.body && <p className="leading-relaxed">{item.body.substring(0, 100)}...</p>}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};
